// components/hr/DocumentUploadModal.tsx
import React, { useRef, useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  Option,
  Button,
  Alert,
  Typography,
  Checkbox,
} from '@mui/joy';
import {
  UploadSimple as UploadOutlined,
  CheckCircle as CheckCircleOutlined,
} from '@phosphor-icons/react';
import { apiClient } from '../../lib/api';
import type { DocumentType } from '../../types/hr';

interface DocumentUploadModalProps {
  open: boolean;
  onClose: () => void;
  memberId: string;
  memberName: string;
  /** The member's open onboarding session, when they have one. */
  sessionId?: string;
  onUploaded: () => void;
}

// Mirrors hr.models.DocumentType. Signed contracts are produced by the signing
// flow rather than filed by hand, so that choice is not offered here.
const documentTypeOptions: { value: DocumentType; label: string }[] = [
  { value: 'w2', label: 'W-2 Wage Statement' },
  { value: '1099', label: '1099 Tax Form' },
  { value: 'disciplinary', label: 'Disciplinary Record' },
  { value: 'recognition', label: 'Award / Recognition' },
  { value: 'id_document', label: 'ID Document' },
  { value: 'drivers_license', label: "Driver's License" },
  { value: 'passport', label: 'Passport' },
  { value: 'ssn_card', label: 'Social Security Card' },
  { value: 'work_authorization', label: 'Work Authorization' },
  { value: 'w4', label: 'W-4 Tax Form' },
  { value: 'w9', label: 'W-9 Tax Form' },
  { value: 'i9', label: 'I-9 Employment Verification' },
  { value: 'direct_deposit', label: 'Direct Deposit Authorization' },
  { value: 'handbook_acknowledgment', label: 'Employee Handbook Acknowledgment' },
  { value: 'nda', label: 'Non-Disclosure Agreement' },
  { value: 'non_compete', label: 'Non-Compete Agreement' },
  { value: 'other', label: 'Other' },
];

const getUploadErrorMessage = (err: unknown): string => {
  const data = (err as { responseData?: Record<string, unknown> })?.responseData;
  if (data) {
    if (typeof data.detail === 'string') return data.detail;
    const [field, value] = Object.entries(data)[0] || [];
    // A document is still stored against an onboarding session, so a member who
    // has never been invited has nothing to file against.
    if (field === 'session') {
      return 'This team member has no onboarding session yet, so a document cannot be filed for them.';
    }
    if (value) return Array.isArray(value) ? String(value[0]) : String(value);
  }
  return 'Failed to upload document. Please try again.';
};

export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  open,
  onClose,
  memberId,
  memberName,
  sessionId,
  onUploaded,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [documentType, setDocumentType] = useState<DocumentType>('other');
  const [expiresAt, setExpiresAt] = useState('');
  const [employeeVisible, setEmployeeVisible] = useState(false);
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const resetForm = () => {
    setFile(null);
    setName('');
    setDocumentType('other');
    setExpiresAt('');
    setEmployeeVisible(false);
    setError('');
  };

  const handleClose = () => {
    if (isUploading) return;
    resetForm();
    onClose();
  };

  const handleFileSelect = (selected: File | null) => {
    if (!selected) return;
    setFile(selected);
    // Default the display name to the file name - HR can still rename it.
    if (!name.trim()) {
      setName(selected.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleSubmit = async () => {
    if (!file || !name.trim()) return;

    setError('');
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('team_member', memberId);
      if (sessionId) {
        formData.append('session', sessionId);
      }
      // An upload satisfies no required template. Sent explicitly because the
      // (session, contract_template) uniqueness rule makes the serializer treat
      // the field as required; an empty value is read as null.
      formData.append('contract_template', '');
      formData.append('document_type', documentType);
      formData.append('name', name.trim());
      formData.append('file', file);
      formData.append('is_hr_only', String(!employeeVisible));
      if (expiresAt) {
        // A licence is good through the end of its expiry date, not from midnight.
        formData.append('expires_at', `${expiresAt}T23:59:59`);
      }

      await apiClient.post('/api/hr/onboarding-documents/', formData);
      resetForm();
      onUploaded();
      onClose();
    } catch (err) {
      setError(getUploadErrorMessage(err));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog sx={{ maxWidth: 480, width: 'calc(100% - 24px)', maxHeight: 'calc(100dvh - 24px)' }}>
        <ModalClose disabled={isUploading} />
        <DialogTitle>Upload Document</DialogTitle>

        <DialogContent sx={{ minHeight: 0, overflowY: 'auto' }}>
          <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
            Filed to {memberName}'s personnel record.
          </Typography>

          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl required>
              <FormLabel>File</FormLabel>
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 'sm',
                  p: 2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: 'primary.400' },
                }}
              >
                <UploadOutlined size={24} style={{ opacity: 0.6 }} />
                <Typography level="body-sm" sx={{ mt: 1 }}>
                  {file ? file.name : 'Click to choose a file'}
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic"
                style={{ display: 'none' }}
                onChange={(e) => handleFileSelect(e.target.files?.[0] || null)}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Document Name</FormLabel>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., OSHA-30 Card"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Document Type</FormLabel>
              <Select
                value={documentType}
                onChange={(_, value) => value && setDocumentType(value)}
              >
                {documentTypeOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </FormControl>

            <Checkbox checked={employeeVisible} onChange={e => setEmployeeVisible(e.target.checked)} label="Show in this employee’s My Employee File" />
            <Typography level="body-xs">Unchecked documents remain HR-only. The employee can view and download shared files, but cannot edit or delete them.</Typography>
            <FormControl>
              <FormLabel>Expires</FormLabel>
              <Input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <FormHelperText>
                Optional. Certificates and licences are flagged as they come due.
              </FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isUploading}
            startDecorator={<CheckCircleOutlined />}
            disabled={!file || !name.trim()}
          >
            Upload
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
};

export default DocumentUploadModal;
